import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import {
  TableView,
  AbstractTableViewsService,
} from '@tiltgroup/web-components';
import {
  Mutation,
  Query,
  TableViewSort,
  UpsertTableViewGroupInput,
} from '@graphql-nexus-types';
import { SortDirection } from '@angular/material/sort';
import { MultiSort, multiSort } from '@tiltgroup/web-components';
import { map, tap } from 'rxjs/operators';
import { ApolloQueryResult } from '@apollo/client/core';
import { ApolloCacheService } from '@shared/services/apollo-cache.service';
import { QueriesBaseService } from '@services/queries/queries-base.service';
import {
  deleteTableViewMutation,
  setTableViewAsDefaultForGroupMutation,
  tableViewGroupQuery,
  unsetDefaultTableViewForGroupMutation,
  upsertTableViewGroupMutation,
} from '@services/queries/queries/table-views.queries';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TableViewsService
  extends QueriesBaseService
  implements AbstractTableViewsService
{
  private _apollo = inject(Apollo);
  private _apolloCache = inject(ApolloCacheService);

  /**
   * Cached `cache-first` — busted via `ApolloCacheService.evictTableView`
   * whenever the user changes column width, selection, or order (see
   * `CacheBustingBaseTable`), and via `evictAllTableViews` after every view
   * mutation below. No TTL: busting is purely mutation-driven.
   */
  getUserTableViews(
    name: string
  ): Observable<{ groupID: string; tableViews: TableView[] }> {
    return this._apollo
      .query({
        query: tableViewGroupQuery,
        variables: {
          name,
        },
        fetchPolicy: 'cache-first',
      })
      .pipe(
        map((res: ApolloQueryResult<Query>) => res.data?.tableViewGroup),
        map((tableViewGroup) => ({
          groupID: tableViewGroup?.id,
          tableViews: (tableViewGroup?.tableViews || []).map((tableView) => ({
            ...tableView,
            default: tableViewGroup.defaultTableView?.name === tableView?.name,
            ...(tableView?.sorts && {
              sort: this.toMultiSort(tableView.sorts),
            }),
            ...(tableView?.queryParameters && {
              queryParameters: JSON.stringify(tableView?.queryParameters),
            }),
            pinnedFilters: tableView?.pinnedFilters,
          })) as TableView[],
        }))
      );
  }

  updateUserTableView(input: UpsertTableViewGroupInput) {
    return this._apollo
      .mutate({
        mutation: upsertTableViewGroupMutation,
        variables: {
          input,
        },
      })
      .pipe(
        tap(() => this._apolloCache.evictAllTableViews()),
        map((res: ApolloQueryResult<Mutation>) => res.data.upsertTableViewGroup)
      );
  }

  /**
   * The saved sort as a `MultiSort`: `TableView.sorts` holds the columns in
   * priority order, each with its own direction. A column saved without a
   * direction is skipped. Undefined when the view has no sort, so `BaseTable`
   * falls back to the table's default sort.
   */
  private toMultiSort(sorts: TableViewSort[]): MultiSort | undefined {
    const entries = sorts.flatMap(({ column, direction }) =>
      column && direction
        ? [
            {
              active: column,
              direction: direction.toLowerCase() as SortDirection,
            },
          ]
        : []
    );

    return entries.length ? multiSort(entries) : undefined;
  }

  deleteUserTableView(id: string) {
    return this._apollo
      .mutate({
        mutation: deleteTableViewMutation,
        variables: {
          id,
        },
      })
      .pipe(
        tap(() => this._apolloCache.evictAllTableViews()),
        map((res: ApolloQueryResult<Mutation>) => res.data.deleteTableView)
      );
  }

  setTableViewAsDefault({
    isDefault,
    isSystem,
    groupId,
    groupName,
    tableViewName,
    tableViewId,
  }: {
    isDefault: boolean;
    isSystem: boolean;
    groupId: string;
    groupName: string;
    tableViewName?: string;
    tableViewId?: string;
  }) {
    if (isDefault) {
      return this._setTableViewAsDefault(
        groupName,
        isSystem,
        tableViewName,
        tableViewId
      );
    } else {
      return this._unsetDefaultTableView(groupId);
    }
  }

  private _setTableViewAsDefault(
    groupName: string,
    isSystem: boolean,
    tableViewName: string,
    tableViewId?: string
  ) {
    return this._apollo
      .mutate({
        mutation: setTableViewAsDefaultForGroupMutation,
        variables: {
          groupName,
          isSystem,
          tableViewName,
          tableViewId,
        },
      })
      .pipe(
        tap(() => this._apolloCache.evictAllTableViews()),
        map(
          (res: ApolloQueryResult<Mutation>) =>
            res.data.setTableViewAsDefaultForGroup
        )
      );
  }

  private _unsetDefaultTableView(groupId: string) {
    return this._apollo
      .mutate({
        mutation: unsetDefaultTableViewForGroupMutation,
        variables: {
          groupId,
        },
      })
      .pipe(
        tap(() => this._apolloCache.evictAllTableViews()),
        map(
          (res: ApolloQueryResult<Mutation>) =>
            res.data.unsetDefaultTableViewForGroup
        )
      );
  }
}
